import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './manageProjects.css';
import { BsPencilSquare, BsTrash } from 'react-icons/bs';
import ProjectDetails from './projectDetails/projectDetails';
import AddProjectModal from './addProjectModal/addProjectModal';
import DeleteConfirmationModal from './deleteWindow/deleteWindow';
import { useTranslation } from 'react-i18next';
import axiosInstance from '../../utils/axiosInstance';
import { toast } from 'react-toastify';
import { Spinner, Alert } from 'react-bootstrap';


const ManageProjects = ({ adminIds, loggedInEmployeeId, empRole }) => {

  const { t } = useTranslation();

  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [employeeProjectRelations, setEmployeeProjectRelations] = useState([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [projectIdToDelete, setProjectIdToDelete] = useState(null);
  const [editProject, setEditProject] = useState(null);
  const [searchTerm, setSearchTerm] = useState(""); // New state for search term
  const [loading, setLoading] = useState(true); // Loading state

  const employeeColors = ['#ff6b6b', '#48dbfb', '#1dd1a1', '#feca57', '#5f27cd'];

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/admin/allProjectInfo');
      const allProjects = response.data;

      // Backend enforces hierarchy-based filtering for Admin vs SuperAdmin.
      const projectData = allProjects.map(project => ({
        id: project.project_id,
        name: project.project_name || '',
        description: project.project_desc || '',
        startDate: project.start_date ? project.start_date.split('T')[0] : '',
        endDate: project.end_date ? project.end_date.split('T')[0] : '',
        timeSpent: 0,
        employees: []
      }));

      setProjects(projectData);
    } catch (error) {
      // Check if error response is available
      if (!error.response) {
        // Network error or no response
        toast.error("Looks like you are offline. Please check your internet connectivity.");
      } else {
        console.error('Error fetching project data:', error);
      }
    } finally {
      setLoading(false);
    }
  };


  const fetchEmployees = async () => {
    try {
      const response = await axiosInstance.get('/admin/employees');
      setEmployees(response.data);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    }
  };

  const fetchEmployeeProjectRelations = async () => {
    try {
      const response = await axiosInstance.get('/admin/empProjRelation');
      setEmployeeProjectRelations(response.data);
    } catch (error) {
      console.error('Error fetching employee-project relations:', error);
    }
  };

  const handleProjectSave = async (newProject) => {
    try {
      const response = await axiosInstance.post('/admin/addProject', newProject);
      const addedProject = response.data;
      setProjects([...projects, addedProject]);
      setIsModalOpen(false);
      await fetchProjects();
      await fetchEmployeeProjectRelations();
    } catch (error) {
      console.error('Error adding project:', error);
    }
  };

  const handleProjectEdit = async (updatedProject) => {
    try {
      const response = await axiosInstance.put(`/admin/updateProject/${updatedProject.id}`, updatedProject);
      setProjects(projects.map(project => project.id === updatedProject.id ? updatedProject : project));
      setIsModalOpen(false);
      await fetchProjects();
      await fetchEmployeeProjectRelations();
    } catch (error) {
      console.error('Error updating project:', error);
    }
  };

  const handleDeleteProject = (projectId) => {
    setProjectIdToDelete(projectId);
    setShowConfirmation(true);
  };

  const confirmDeleteProject = async () => {
    try {
      const response = await axiosInstance.delete(`/admin/deleteProject/${projectIdToDelete}`);
      //console.log(response.data.message);
      setShowConfirmation(false);
      fetchProjects();
      setProjects(projects.filter(project => project.id !== projectIdToDelete));
      window.location.reload();
    } catch (error) {
      console.error('Error deleting project:', error);
    }
  };

  const cancelDeleteProject = () => {
    setShowConfirmation(false);
  };

  const handleProjectClick = (project) => {
    setSelectedProject(project);
  };

  const handleEditProjectClick = (project) => {
    setEditProject(project);
    setIsModalOpen(true);
  };

  const truncateText = (text, length) => {
    if (!text || typeof text !== 'string') {
      return '';
    }
    return text.length > length ? `${text.substring(0, length)}...` : text;
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value.toLowerCase());
  };

  // Derive "projects with employees" without mutating state (prevents update loops).
  const projectsWithEmployees = useMemo(() => {
    if (projects.length === 0) return projects;
    if (employees.length === 0 || employeeProjectRelations.length === 0) return projects;

    return projects.map(project => {
      const relatedEmployees = employeeProjectRelations
        .filter(
          relation =>
            relation.project_id === project.id && relation.employee_status === 'Active'
        )
        .map(relation => {
          const employee = employees.find(emp => emp.emp_id === relation.emp_id);
          return employee
            ? { id: employee.emp_id, name: employee.p_name, sap_id: employee.emp_sap_id }
            : null;
        })
        .filter(Boolean);

      return { ...project, employees: relatedEmployees };
    });
  }, [projects, employees, employeeProjectRelations]);

  // Filter projects based on the search term
  const filteredProjects = searchTerm
    ? projectsWithEmployees.filter(project => {
      const projectNameMatch = project.name.toLowerCase().includes(searchTerm);
      const projectDescMatch = project.description.toLowerCase().includes(searchTerm);
      const employeeMatch = project.employees.some(employee =>
        employee.name.toLowerCase().includes(searchTerm)
      );
      return projectNameMatch || projectDescMatch || employeeMatch;
    })
    : projectsWithEmployees;  // Display all projects when no search term

  useEffect(() => {
    fetchProjects();
    fetchEmployees();
    fetchEmployeeProjectRelations();
  }, [loggedInEmployeeId, empRole]);

  // const filteredProjects = projects.filter(project => {
  //   const projectNameMatch = project.name.toLowerCase().includes(searchTerm);
  //   const projectDescMatch = project.description.toLowerCase().includes(searchTerm);
  //   const employeeMatch = project.employees.some(employee =>
  //     employee.name.toLowerCase().includes(searchTerm)
  //   );
  //   return projectNameMatch || projectDescMatch || employeeMatch;
  // });

  return (
    <div className="manageProjects">
      {selectedProject ? (
        <ProjectDetails
          adminIds={adminIds}
          loggedInEmployeeId={loggedInEmployeeId}
          project={selectedProject}
          onBack={() => setSelectedProject(null)}
        />
      ) : (
        <>
          <h2 className='pgTitle'>{t("ManageProjectTitle")}</h2>
          <div className='addAndSearch'>
            <button className="addProjectButton" onClick={() => setIsModalOpen(true)}>
              {t("manageProject_addProject")}
            </button>
            <input
              type="text"
              className='addAndSearch-input'
              placeholder={t('Search here')}
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="projectCards">
            {loading ? (
              <div className="manageProjects-Spinner">
                <Spinner animation="border" role="status">
                  <span className=""></span>
                </Spinner><br />
                <span className="mt-2">{t('Please wait your data is loading')}</span>
              </div>
            ) : filteredProjects.length > 0 ? (
              filteredProjects.map(project => (
                <div className="projectCard" key={project.id} onClick={() => handleProjectClick(project)}>
                  <div className="projectHeaders">
                    <h3>{project.name}</h3>
                  </div>
                  <p className="projectDescription" title={project.description}>
                    {truncateText(project.description, 35)}
                  </p>
                  <div className="employeeList">
                    {project.employees.map((employee, index) => (
                      <div
                        className="employeeInitial"
                        key={employee.id}
                        style={{ backgroundColor: employeeColors[index % employeeColors.length] }}
                      >
                        {employee.name.split(' ').map(name => name[0].toUpperCase()).join('')}
                      </div>
                    ))}
                  </div>
                  <div className="projectActions">
                    <BsPencilSquare
                      className="editIcon"
                      title="Edit Project"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditProjectClick(project);
                      }}
                    />
                    <BsTrash
                      className="deleteIcon"
                      title="Delete Project"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteProject(project.id);
                      }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <Alert variant="light" className='manageProjects-Alert'>
                {t("No projects data found")}
              </Alert>
            )}
          </div>
        </>
      )}
      {isModalOpen && (
        <AddProjectModal
          project={editProject}
          employees={employees}
          adminIds={adminIds}
          empRole={empRole}
          loggedInEmployeeId={loggedInEmployeeId}
          onClose={() => {
            setIsModalOpen(false);
            setEditProject(null);
          }}
          onSave={editProject ? handleProjectEdit : handleProjectSave}
        />
      )}
      <DeleteConfirmationModal
        show={showConfirmation}
        onHide={cancelDeleteProject}
        onConfirm={confirmDeleteProject}
      />
    </div>
  );
};

export default ManageProjects;